import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/footer";
import WhatsAppButton from "../components/WhatsAppButton";
import SeoHead from "../components/SeoHead";
import OptimizedImage from "../components/OptimizedImage";
import siteData from "../data/siteData";
import blogPosts from "../data/blogData";
import blogFallbackImage from "../assets/img/ai-automationhero.png";
import { createBreadcrumbList } from "../utils/seo";
import { getBlogImage } from "../utils/blogImages";

const categories = ["All", ...new Set(blogPosts.map((post) => post.category))];

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const estimateReadingTime = (contentHtml) => {
  const words = contentHtml.replace(/<[^>]*>/g, " ").split(/\s+/).filter(Boolean).length;
  return `${Math.max(3, Math.ceil(words / 200))} min read`;
};

const QbitsBlog = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredPosts = useMemo(() => {
    if (selectedCategory === "All") return blogPosts;
    return blogPosts.filter((post) => post.category === selectedCategory);
  }, [selectedCategory]);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "QBITS9 Blog",
      url: `${siteData.siteUrl}/blog`,
      description:
        "QBITS9 blog shares AI automation, web development, SaaS, ERP, and D2C brand technology insights for Indian businesses.",
      publisher: {
        "@type": "Organization",
        name: siteData.siteName,
        url: siteData.siteUrl,
        logo: {
          "@type": "ImageObject",
          url: `${siteData.siteUrl}${siteData.defaultShareImage}`,
        },
      },
    },
    createBreadcrumbList([
      { name: "Home", path: "/" },
      { name: "Blog", path: "/blog" },
    ]),
  ];

  return (
    <div className="min-h-screen bg-background text-slate-900">
      <SeoHead
        title="QBITS9 Blog | AI Automation and Digital Solutions"
        description="QBITS9 blog shares AI automation, web development, SaaS, ERP, and D2C brand technology insights for Indian businesses."
        canonicalPath="/blog"
        image={blogFallbackImage}
        imageAlt="QBITS9 blog and AI automation insights"
        keywords={["AI automation blog", "small business automation", "workflow automation India", "SaaS development insights", "ERP development guide", "AI for small business"]}
        jsonLd={jsonLd}
      />

      <Header />

      <main className="min-h-screen bg-background">
        <section className="bg-gradient-to-br from-primary/10 via-secondary/5 to-background py-16 md:py-24">
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            <span className="inline-flex rounded-full border border-primary/20 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-primary shadow-sm">
              QBITS9 Insights
            </span>
            <h1 className="mt-5 max-w-4xl text-4xl font-unbounded font-bold text-black md:text-5xl lg:text-6xl">
              AI automation, SaaS, and digital growth insights for Indian businesses.
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-grey font-poppins">
              Practical articles on AI automation for small business India, web and mobile development, SaaS and ERP systems, and D2C brand operations.
            </p>
          </div>
        </section>

        <section className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="flex flex-col gap-4 items-center">
            <p className="max-w-3xl text-center text-sm md:text-base leading-7 text-grey font-poppins">
              If you are exploring <Link to="/services/ai-automation" className="text-primary hover:text-secondary underline">AI automation services</Link> for a small business, these articles will help you find the right workflow to automate first. You can also <Link to="/contact" className="text-primary hover:text-secondary underline">book a free automation audit</Link> to get a practical starting point.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-5 py-2.5 rounded-full font-poppins font-medium transition-all duration-300 ${
                    selectedCategory === category
                      ? "bg-primary text-white shadow-lg scale-105"
                      : "bg-white text-grey border-2 border-grey/20 hover:border-primary hover:text-primary hover:shadow-md"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {selectedCategory !== "All" ? (
              <div className="flex flex-wrap items-center justify-center gap-2 text-sm font-poppins text-grey max-w-2xl">
                <span>Showing:</span>
                <span className="font-semibold text-primary">{selectedCategory}</span>
                <button
                  onClick={() => setSelectedCategory("All")}
                  className="ml-2 text-primary hover:text-secondary underline whitespace-nowrap"
                >
                  Clear filter
                </button>
              </div>
            ) : null}
          </div>
        </section>

        <section className="container mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
          {filteredPosts.length === 0 ? (
            <div className="flex items-center justify-center min-h-[300px]">
              <div className="text-center">
                <p className="text-2xl text-grey font-poppins mb-4">
                  No posts found for this category.
                </p>
                <button
                  onClick={() => setSelectedCategory("All")}
                  className="px-6 py-3 bg-primary text-white rounded-lg font-poppins font-medium hover:bg-secondary transition-colors"
                >
                  View All Posts
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filteredPosts.map((post) => (
                <article
                  key={post.slug}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300 flex flex-col"
                >
                  <div className="relative w-full h-56 bg-grey/10 overflow-hidden">
                    <OptimizedImage
                      src={getBlogImage(post, blogFallbackImage)}
                      alt={post.title}
                      width="1200"
                      height="630"
                      className="h-full w-full object-cover hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>

                  <div className="p-6 flex flex-col flex-grow">
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="inline-block px-3 py-1 text-xs font-poppins font-medium bg-primary/10 text-primary rounded-full">
                        {post.category}
                      </span>
                    </div>

                    <h2 className="text-xl md:text-2xl font-unbounded font-bold text-black mb-3 line-clamp-2">
                      {post.title}
                    </h2>

                    <p className="text-grey font-poppins text-sm md:text-base mb-4 line-clamp-3 flex-grow">
                      {post.excerpt}
                    </p>

                    <div className="flex items-center justify-between gap-4 pt-4 border-t border-grey/20">
                      <div className="space-y-1">
                        <time dateTime={post.datePublished} className="text-sm text-grey font-poppins block">
                          {formatDate(post.datePublished)}
                        </time>
                        <span className="text-xs text-grey/70 font-poppins">
                          {estimateReadingTime(post.contentHtml)}
                        </span>
                      </div>
                      <Link
                        to={`/blog/${post.slug}`}
                        aria-label={`Read more about ${post.title}`}
                        className="inline-flex items-center text-primary font-poppins font-medium hover:text-secondary transition-colors"
                      >
                        Read More
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default QbitsBlog;
